import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { motion } from 'framer-motion';
import { notification } from 'antd';
import PhoneInput from 'react-phone-number-input/input';
import { Hand, PhoneCall, ClipboardList, Users, ArrowRight } from 'lucide-react';

import { isValidEmail } from '../../helper';
import { postYourCampService, getPageDataService } from '../../services/otherService';

const BRAND = '#842014';
const BRAND_DARK = '#6b1a10';

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware',
  'Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky',
  'Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi',
  'Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico',
  'New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania',
  'Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
  'Virginia','Washington','West Virginia','Wisconsin','Wyoming',
];

const inputStyle = {
  width: '100%',
  padding: '12px 16px',
  backgroundColor: 'rgba(255,255,255,0.92)',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  outline: 'none',
  color: '#111827',
  fontSize: '0.875rem',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const inputErrStyle = { ...inputStyle, borderColor: '#f87171' };

const errText = {
  fontSize: '0.75rem',
  color: '#fca5a5',
  marginTop: '4px',
  display: 'block',
};

// ─── HeroWithForm ─────────────────────────────────────────────────────────────
function HeroWithForm({ preList, onSubmitSuccess }) {
  const [form, setForm] = useState({
    firstName: '', lastName: '', campName: '', category: '',
    email: '', phone: '', state: '', averageAttendee: '', comment: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    else if (form.firstName.length > 50) e.firstName = 'Max 50 characters';
    if (!form.campName.trim()) e.campName = 'Organization name is required';
    if (!form.category) e.category = 'Category is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!isValidEmail(form.email)) e.email = 'Email is not valid';
    if (!form.phone) e.phone = 'Phone number is required';
    else if (form.phone.replace(/\D/g, '').length !== 11) e.phone = 'Phone number must be exactly 10 digits';
    if (!form.state) e.state = 'State is required';
    if (!form.averageAttendee) e.averageAttendee = 'Average attendance is required';
    return e;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    postYourCampService({
      firstName: form.firstName, lastName: form.lastName, state: form.state,
      campName: form.campName, category: form.category,
      averageAttendee: form.averageAttendee, phone: form.phone,
      email: form.email, comment: form.comment,
    }).then(res => {
      setSubmitting(false);
      if (res?.data?.statusCode) {
        setForm({ firstName:'',lastName:'',campName:'',category:'',email:'',phone:'',state:'',averageAttendee:'',comment:'' });
        notification.open({ message: 'Success', description: 'Your program has been submitted successfully!' });
        onSubmitSuccess?.();
      }
    }).catch(() => {
      setSubmitting(false);
      notification.open({ message: 'Error', description: 'Something went wrong. Please try again.' });
    });
  };

  const field = (name, placeholder, type = 'text') => (
    <input
      type={type} name={name} placeholder={placeholder}
      value={form[name]} onChange={handleChange}
      style={errors[name] ? inputErrStyle : inputStyle}
      required
    />
  );

  return (
    <section style={{ position: 'relative', width: '100%', minHeight: '700px', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url(https://images.unsplash.com/photo-1705466437774-7af6ffc79750?w=1600&h=900&fit=crop)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.65)' }} />

      <div style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: '1280px', margin: '0 auto', padding: '64px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', alignItems: 'center' }} className="list-camp-hero-grid">
        {/* Left */}
        <div>
          <motion.h1 initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.8, ease:'easeOut' }}
            style={{ fontSize:'clamp(2.5rem,5vw,3.75rem)', fontWeight:'800', color:'#fff', marginBottom:'24px', lineHeight:1.15 }}>
            List Your Program
          </motion.h1>
          <motion.p initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.8, delay:0.2, ease:'easeOut' }}
            style={{ fontSize:'1.25rem', color:'rgba(255,255,255,0.9)', marginBottom:'32px', lineHeight:1.7, maxWidth:'480px' }}>
            Connect with families in your community. Let us help you reach the people who matter most and grow your program effortlessly.
          </motion.p>
          <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.8, delay:0.4, ease:'easeOut' }}
            style={{ display:'flex', alignItems:'center', gap:'24px', color:'rgba(255,255,255,0.7)', fontSize:'0.875rem', flexWrap:'wrap' }}>
            {['Manage Quickly','Reach Thousands','Easy Setup'].map(t => (
              <div key={t} style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <div style={{ width:'8px', height:'8px', borderRadius:'50%', backgroundColor:BRAND }} />
                {t}
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right — Form */}
        <motion.div initial={{ opacity:0, x:40 }} animate={{ opacity:1, x:0 }} transition={{ duration:0.8, delay:0.3, ease:'easeOut' }}>
          <form onSubmit={handleSubmit} style={{ backgroundColor:'rgba(255,255,255,0.1)', backdropFilter:'blur(16px)', borderRadius:'16px', padding:'32px', boxShadow:'0 25px 50px rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.2)' }}>
            <h2 style={{ fontSize:'1.5rem', fontWeight:'700', color:'#fff', marginBottom:'6px' }}>Let Us Know About You</h2>
            <p style={{ color:'rgba(255,255,255,0.65)', fontSize:'0.875rem', marginBottom:'24px' }}>Share your details and we'll help you get started</p>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'12px' }}>
              <div>{field('firstName','First Name')}{errors.firstName && <span style={errText}>{errors.firstName}</span>}</div>
              <div>{field('lastName','Last Name')}</div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'12px' }}>
              <div>
                {field('campName','Organization Name')}
                {errors.campName && <span style={errText}>{errors.campName}</span>}
              </div>
              <div>
                <select name="category" value={form.category} onChange={handleChange} style={errors.category ? inputErrStyle : inputStyle}>
                  <option value="">Select Category</option>
                  {preList?.category?.map(item => <option key={item._id} value={item._id}>{item.name}</option>)}
                </select>
                {errors.category && <span style={errText}>{errors.category}</span>}
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'12px' }}>
              <div>
                {field('email','Email','email')}
                {errors.email && <span style={errText}>{errors.email}</span>}
              </div>
              <div>
                <PhoneInput country="US" placeholder="Phone" value={form.phone} maxLength={16}
                  onChange={val => { setForm(p=>({...p,phone:val||''})); if(errors.phone) setErrors(p=>({...p,phone:''})); }}
                  style={errors.phone ? inputErrStyle : inputStyle} />
                {errors.phone && <span style={errText}>{errors.phone}</span>}
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'12px' }}>
              <div>
                <select name="state" value={form.state} onChange={handleChange} style={errors.state ? inputErrStyle : inputStyle}>
                  <option value="">Select State</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {errors.state && <span style={errText}>{errors.state}</span>}
              </div>
              <div>
                {field('averageAttendee','Avg. Annual Attendance')}
                {errors.averageAttendee && <span style={errText}>{errors.averageAttendee}</span>}
              </div>
            </div>
            <div style={{ marginBottom:'20px' }}>
              <textarea name="comment" placeholder="Tell us more about your program..." value={form.comment} onChange={handleChange}
                rows={3} style={{ ...inputStyle, resize:'none' }} />
            </div>
            <motion.button whileHover={{ scale:1.02 }} whileTap={{ scale:0.98 }} type="submit" disabled={submitting}
              style={{ width:'100%', padding:'14px', backgroundColor:BRAND, color:'#fff', fontWeight:'600', borderRadius:'8px', border:'none', cursor:submitting?'not-allowed':'pointer', fontSize:'1rem', opacity:submitting?0.7:1, boxShadow:'0 4px 14px rgba(132,32,20,0.4)' }}>
              {submitting ? 'Submitting…' : 'Submit'}
            </motion.button>
          </form>
        </motion.div>
      </div>

      <style>{`.list-camp-hero-grid { grid-template-columns: 1fr 1fr; } @media(max-width:768px){.list-camp-hero-grid{grid-template-columns:1fr !important;}}`}</style>
    </section>
  );
}

// ─── Features ─────────────────────────────────────────────────────────────────
const FALLBACK_FEATURES = [
  { title:'Engage With Families Daily', content:'Build meaningful connections with families in your community. Our platform makes it easy to engage with parents, share updates, and keep everyone informed about your programs and activities.', image:'https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=800&h=600&fit=crop' },
  { title:'Interact With Your Closest Customers', content:'Direct communication tools that help you stay connected with your biggest supporters. Respond to inquiries, share program details, and build lasting relationships with the families you serve.', image:'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&h=600&fit=crop' },
  { title:'Focus On What You Are An Expert At!', content:"Let CampsEZ handle the technology while you focus on delivering exceptional programs. We take care of listings, registrations, and communications so you can do what you do best.", image:'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&h=600&fit=crop' },
  { title:'Display Your Amazing Programs', content:'Showcase your programs to thousands of families actively searching for quality youth activities. Our platform gives you the visibility you need to reach more participants and grow your impact.', image:'https://images.unsplash.com/photo-1551958219-acbc608c6377?w=800&h=600&fit=crop' },
];

function FeatureItem({ title, content, image, reverse, index }) {
  return (
    <motion.div
      initial={{ opacity:0, y:50 }} whileInView={{ opacity:1, y:0 }}
      viewport={{ once:true, margin:'-100px' }} transition={{ duration:0.6, ease:'easeOut' }}
      style={{ display:'flex', flexDirection: reverse ? 'row-reverse' : 'row', alignItems:'center', gap:'32px', marginBottom:'56px', flexWrap:'wrap' }}
    >
      <motion.div initial={{ opacity:0, scale:0.9 }} whileInView={{ opacity:1, scale:1 }} viewport={{ once:true }} transition={{ duration:0.6, delay:0.2 }}
        style={{ width:'40%', minWidth:'260px', flexShrink:0, borderRadius:'12px', overflow:'hidden', boxShadow:'0 20px 40px rgba(0,0,0,0.12)' }}>
        <img src={image} alt={title} style={{ width:'100%', height:'260px', objectFit:'cover', display:'block' }} />
      </motion.div>
      <div style={{ flex:1, minWidth:'240px' }}>
        <motion.h3 initial={{ opacity:0, x: reverse?20:-20 }} whileInView={{ opacity:1, x:0 }} viewport={{ once:true }} transition={{ duration:0.6, delay:0.1 }}
          style={{ fontSize:'clamp(1.5rem,3vw,2.25rem)', fontWeight:'700', color:'#111827', marginBottom:'12px', lineHeight:1.25 }}>
          {title}
        </motion.h3>
        <motion.p initial={{ opacity:0, x: reverse?20:-20 }} whileInView={{ opacity:1, x:0 }} viewport={{ once:true }} transition={{ duration:0.6, delay:0.2 }}
          style={{ fontSize:'1.0625rem', color:'#4b5563', lineHeight:1.75, margin:0 }}>
          {content}
        </motion.p>
      </div>
    </motion.div>
  );
}

function Features({ apiSections }) {
  const features = apiSections.length
    ? apiSections.map(s => ({ title: s.title, content: s.content, image: s.image }))
    : FALLBACK_FEATURES;

  return (
    <section style={{ width:'100%', padding:'64px 24px', background:'linear-gradient(to bottom, #fff, #f9fafb)' }}>
      <div style={{ maxWidth:'1152px', margin:'0 auto' }}>
        {features.map((f, i) => <FeatureItem key={i} {...f} reverse={i%2===1} index={i} />)}
      </div>
    </section>
  );
}

// ─── HowItWorks ───────────────────────────────────────────────────────────────
const HOW_STEPS = [
  { icon: <Hand size={40} />, title:'Say Hello', description:'Fill out the information and let us know about you and your program!' },
  { icon: <PhoneCall size={40} />, title:'We Reach Out To You', description:"We'll contact you for a quick introduction to get to know you and your organization personally." },
  { icon: <ClipboardList size={40} />, title:'Enter Program Information', description:'Once your account is created, begin entering all your amazing activities and programs.' },
  { icon: <Users size={40} />, title:'Greet Participants', description:'(The best part!) People enjoying the fun activities, programs, and camps that you provide!' },
];

function HowItWorks() {
  return (
    <section style={{ width:'100%', padding:'96px 24px', backgroundColor:'#fff' }}>
      <div style={{ maxWidth:'1280px', margin:'0 auto' }}>
        <motion.h2 initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} transition={{ duration:0.6 }}
          style={{ fontSize:'clamp(2rem,4vw,3rem)', fontWeight:'700', textAlign:'center', color:'#111827', marginBottom:'64px' }}>
          How It Works
        </motion.h2>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'48px' }}>
          {HOW_STEPS.map((step, i) => (
            <motion.div key={i} initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} transition={{ duration:0.6, delay:i*0.1 }}
              style={{ display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center', gap:'16px' }}>
              <div style={{ position:'relative' }}>
                <div style={{ width:'80px', height:'80px', borderRadius:'50%', backgroundColor:BRAND, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', boxShadow:'0 8px 20px rgba(132,32,20,0.35)' }}>
                  {step.icon}
                </div>
                <div style={{ position:'absolute', top:'-8px', right:'-8px', width:'28px', height:'28px', borderRadius:'50%', backgroundColor:'#f59e0b', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.8125rem', fontWeight:'700', boxShadow:'0 2px 8px rgba(0,0,0,0.2)' }}>
                  {i+1}
                </div>
              </div>
              <h4 style={{ fontSize:'1.125rem', fontWeight:'700', color:'#111827', margin:0 }}>{step.title}</h4>
              <p style={{ color:'#6b7280', maxWidth:'240px', margin:0, lineHeight:1.6, fontSize:'0.9375rem' }}>{step.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
const ListCampNew = ({ preList }) => {
  const [featureSections, setFeatureSections] = useState([]);

  useEffect(() => {
    getPageDataService('?page=lyc').then(res => {
      if (res?.data?.statusCode) {
        const sections = res.data.responseData.result.filter(
          item => ['lycSection2','lycSection3','lycSection4','lycSection5'].includes(item.type)
        );
        setFeatureSections(sections);
      }
    }).catch(() => {});
  }, []);

  return (
    <div style={{ backgroundColor:'#fff' }}>
      <HeroWithForm preList={preList} />
      <Features apiSections={featureSections} />
      <HowItWorks />
    </div>
  );
};

const mapStateToProps = state => ({
  preList: state.program.preList,
});

export default connect(mapStateToProps)(ListCampNew);
